import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Logo } from './Logo';

export const Header = () => {
  const { lang, toggleLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const links = [
    { to: '/', label: t.nav.home, end: true },
    { to: '/sobre', label: t.nav.about },
    { to: '/servicos', label: t.nav.services },
    { to: '/cases', label: t.nav.portfolio },
    { to: '/contato', label: t.nav.contact },
  ];

  const linkClass = ({ isActive }) =>
    `text-[13px] tracking-wide transition-opacity duration-300 ${
      isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(244, 239, 230, 0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(10px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--rule)' : '1px solid transparent',
        color: 'var(--ink)',
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" aria-label="Lawork" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={linkClass}
                data-testid={`nav-${l.to === '/' ? 'home' : l.to.slice(1)}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-6">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="hidden sm:flex items-center text-[11px] tracking-[0.18em] opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: 'var(--ink)' }}
            >
              <span className={lang === 'pt' ? 'font-medium' : 'opacity-50'}>PT</span>
              <span className="mx-1.5 opacity-40">/</span>
              <span className={lang === 'en' ? 'font-medium' : 'opacity-50'}>EN</span>
            </button>

            <Link
              to="/contato"
              data-testid="header-cta"
              className="hidden md:inline-flex items-center text-[13px] tracking-wide link-underline"
            >
              {t.nav.cta}
            </Link>

            <button
              className="md:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              style={{ color: 'var(--ink)' }}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav
          className="md:hidden px-6 py-6"
          style={{ background: 'var(--paper)', borderTop: '1px solid var(--rule)' }}
        >
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className="font-serif text-2xl"
                style={{ color: 'var(--ink)' }}
                data-testid={`mobile-nav-${l.to === '/' ? 'home' : l.to.slice(1)}`}
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={toggleLang}
              data-testid="mobile-lang-toggle"
              className="text-left text-[11px] tracking-[0.18em] pt-4 opacity-70"
              style={{ color: 'var(--ink)' }}
            >
              <span className={lang === 'pt' ? 'font-medium' : 'opacity-50'}>PT</span>
              <span className="mx-1.5 opacity-40">/</span>
              <span className={lang === 'en' ? 'font-medium' : 'opacity-50'}>EN</span>
            </button>
          </div>
        </nav>
      )}
    </header>
  );
};
